import { isEmpty } from "@/utils/utils";
import { User } from "@repo/types/user";

// 用户对象Key
export const SESSION_KEY = "furries_session";

export namespace UserManager {
  interface StorageItem {
    data: string;
    expire: number;
  }

  /**
   * 保存登录会话（与 AuthProvider / web 端约定一致：{ token, expire }）
   * @param token 登录令牌
   * @param expire 过期时间戳（毫秒），0 表示不按本地过期清理
   */
  export function saveSessionData(token: string, expire: number = 0) {
    if (typeof window === "undefined") {
      return;
    }
    const session: User.Session = { token, expire };
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        data: JSON.stringify(session),
        expire,
      } satisfies StorageItem),
    );
  }

  /**
   * 获取当前登录会话。兼容历史上把 data 存成纯 token 字符串的格式。
   */
  export function getSessionData(): User.Session | undefined {
    if (typeof window === "undefined") {
      return undefined;
    }
    try {
      const storageData = sessionStorage.getItem(SESSION_KEY) || "";
      if (isEmpty(storageData)) {
        return undefined;
      }
      const session: StorageItem = JSON.parse(storageData);
      const nowTimeStamp = Date.now();
      if (session.expire > 0 && session.expire < nowTimeStamp) {
        sessionStorage.removeItem(SESSION_KEY);
        return undefined;
      }
      const parsed: unknown = JSON.parse(session.data);
      if (typeof parsed === "string") {
        return { token: parsed, expire: session.expire };
      }
      if (parsed && typeof parsed === "object" && "token" in parsed) {
        const obj = parsed as User.Session;
        return { token: String(obj.token ?? ""), expire: obj.expire ?? session.expire };
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  /** 取出当前 token 字符串 */
  export function getToken(): string {
    return getSessionData()?.token ?? "";
  }

  /** 仅删除本地 session 并通知上下文，不做跳转 / reload */
  export function removeSession() {
    if (typeof window === "undefined") {
      return;
    }
    sessionStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(
      new CustomEvent("tokenChanged", {
        detail: {
          action: "logout" as const,
          timestamp: Date.now(),
          source: "logout",
        },
      }),
    );
  }

  /**
   * 获取令牌
   * @returns
   */
  export function getHeaderToken() {
    return getSessionData();
  }
}
